import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Table,
  Card,
  Alert,
  Tag,
  Button,
  Space,
  Drawer,
  Popconfirm,
  Typography,
  App as AntApp,
} from 'antd';
import apiClient from '@/services/api';
import { useSession } from '@/hooks/useSession';

const { Title } = Typography;

interface Plan {
  uuid: string;
  productId: string | null;
  name: string | null;
  period: string | null;
  periodUnit: number | null;
  enabled: boolean;
  quantity: number | null;
  expired: string | null;
  start: string | null;
}

interface LicensesResp {
  Result: string;
  count: number;
  plans: Plan[];
}

interface UnitInfo {
  licenseUnitUUID: string;
  licenseUUID: string;
  licenseName: string | null;
  deviceUUID: string;
  start: string | null;
  expired: string | null;
  enabled: boolean;
  status: 'active' | 'suspended' | 'expired';
}

interface UnitsResp {
  Result: string;
  count: number;
  licenseInfo: UnitInfo[];
}

function statusTag(s: UnitInfo['status']): React.ReactNode {
  switch (s) {
    case 'active':
      return <Tag color="green">Active</Tag>;
    case 'suspended':
      return <Tag color="orange">Suspended</Tag>;
    case 'expired':
      return <Tag>Expired</Tag>;
  }
}

export default function VendorHome() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { user } = useSession();
  const queryClient = useQueryClient();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const canManage = user?.role === 'vendor_operator' && !!user?.canSelfManageUnits;

  const plans = useQuery({
    queryKey: ['vendor', 'portal', 'licenses'],
    queryFn: async () => {
      const r = await apiClient.get<LicensesResp>('/vendor/portal/licenses');
      return r as unknown as LicensesResp;
    },
  });

  const units = useQuery({
    queryKey: ['vendor', 'portal', 'units', selectedPlan?.uuid],
    enabled: !!selectedPlan,
    queryFn: async () => {
      const r = await apiClient.get<UnitsResp>(
        `/vendor/portal/licenses/${selectedPlan!.uuid}/units`,
      );
      return r as unknown as UnitsResp;
    },
  });

  const suspendMut = useMutation({
    mutationFn: async (unitId: string) => {
      await apiClient.post(`/vendor/portal/license-units/${unitId}/suspend`);
    },
    onSuccess: () => {
      message.success('Unit suspended');
      queryClient.invalidateQueries({ queryKey: ['vendor', 'portal', 'units'] });
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || e?.response?.data?.Result || String(e);
      message.error(msg);
    },
  });

  const unsuspendMut = useMutation({
    mutationFn: async (unitId: string) => {
      await apiClient.post(`/vendor/portal/license-units/${unitId}/unsuspend`);
    },
    onSuccess: () => {
      message.success('Unit resumed');
      queryClient.invalidateQueries({ queryKey: ['vendor', 'portal', 'units'] });
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || e?.response?.data?.Result || String(e);
      message.error(msg);
    },
  });

  return (
    <>
      <Title level={3} style={{ marginTop: 0 }}>
        {t('vendor.licensesTitle')}
      </Title>
      {plans.error ? (
        <Alert type="error" message="Failed to load plans" description={String(plans.error)} />
      ) : (
        <Card title={`${t('vendor.yourPlans')} (${plans.data?.count ?? 0})`}>
          <Table
            size="small"
            rowKey="uuid"
            loading={plans.isLoading}
            dataSource={plans.data?.plans ?? []}
            pagination={{ pageSize: 20 }}
            onRow={(rec) => ({
              onClick: () => setSelectedPlan(rec),
              style: { cursor: 'pointer' },
            })}
            columns={[
              { title: 'Name', dataIndex: 'name', key: 'name' },
              { title: 'Product ID', dataIndex: 'productId', key: 'pid' },
              {
                title: 'Period',
                key: 'period',
                render: (_, rec) =>
                  rec.period ? `${rec.periodUnit ?? 1} ${rec.period}` : '—',
              },
              { title: 'Quantity', dataIndex: 'quantity', key: 'q', width: 100 },
              {
                title: 'Status',
                key: 'status',
                width: 110,
                render: (_, rec) =>
                  rec.enabled ? <Tag color="green">Active</Tag> : <Tag>Disabled</Tag>,
              },
            ]}
          />
        </Card>
      )}

      <Drawer
        width={780}
        open={!!selectedPlan}
        onClose={() => setSelectedPlan(null)}
        title={selectedPlan ? `Units in ${selectedPlan.name ?? selectedPlan.uuid}` : ''}
      >
        {selectedPlan && (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message={
                canManage
                  ? t('vendor.authorizedMsg')
                  : user?.role === 'vendor_operator' && !user?.canSelfManageUnits
                    ? t('vendor.notAuthorizedMsg')
                    : t('vendor.readOnlyMsg')
              }
            />
            <Table
              size="small"
              rowKey="licenseUnitUUID"
              loading={units.isLoading}
              dataSource={units.data?.licenseInfo ?? []}
              pagination={{ pageSize: 50 }}
              columns={[
                {
                  title: 'Unit UUID',
                  dataIndex: 'licenseUnitUUID',
                  key: 'uid',
                  render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>,
                },
                {
                  title: 'Device',
                  dataIndex: 'deviceUUID',
                  key: 'dev',
                  render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>,
                },
                {
                  title: 'Expiry',
                  dataIndex: 'expired',
                  key: 'exp',
                  render: (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—'),
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  key: 'status',
                  width: 120,
                  render: (s: UnitInfo['status']) => statusTag(s),
                },
                ...(canManage
                  ? [
                      {
                        title: 'Actions',
                        key: 'act',
                        width: 160,
                        render: (_: any, rec: UnitInfo) => (
                          <Space size="small">
                            {rec.status === 'active' && (
                              <Popconfirm
                                title={t('admin.suspend') + '?'}
                                onConfirm={() => suspendMut.mutate(rec.licenseUnitUUID)}
                              >
                                <Button
                                  size="small"
                                  danger
                                  loading={suspendMut.isPending}
                                >
                                  {t('admin.suspend')}
                                </Button>
                              </Popconfirm>
                            )}
                            {rec.status === 'suspended' && (
                              <Popconfirm
                                title={t('admin.resume') + '?'}
                                onConfirm={() => unsuspendMut.mutate(rec.licenseUnitUUID)}
                              >
                                <Button
                                  size="small"
                                  type="primary"
                                  loading={unsuspendMut.isPending}
                                >
                                  {t('admin.resume')}
                                </Button>
                              </Popconfirm>
                            )}
                          </Space>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          </>
        )}
      </Drawer>
    </>
  );
}
